import React, { useEffect, useRef, useState } from 'react';
import './Level3.css'

import Character from '../Assets/Imgs/Character.png';
import StartPlat from '../Assets/Imgs/StartPlatform.png'
import EndPlat from '../Assets/Imgs/EndPlatform.png'
import Control from '../Assets/Imgs/Control.png'
import MovingPlat from '../Assets/Imgs/MovingPlats.png'
import Pause from '../Assets/Icons/Pause.svg' // Imported Pause SVG icon

import Terminal from '../Components/Common/Terminal';
import LoseScreen from '../Components/Layout/LoseScreen'; 
import WinScreen from '../Components/Layout/WinScreen'; 
import Btn from '../Components/Common/Btn'; // Imported common action button
import PauseMenu from '../Components/Layout/PauseMenu'; // Imported Pause Menu overlay wrapper

const Level3 = () => {

    const START_X = 50; 
    const START_Y = 60; 

    const [pos, setPos] = useState({ x: START_X, y: START_Y }); 
    const velocity = useRef({ x: 0, y: 0 });
    const keysPressed = useRef({});

    const [isDead, setIsDead] = useState(false);
    const [isWon, setIsWon] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    const [isNearTerminal, setIsNearTerminal] = useState(false);

    const gameTime = useRef(0);
    const [platY, setPlatY] = useState({ p1: 600, p2: 600, p3: 600 });

    const GRAVITY = 0.1;
    const MOVE_SPEED = 2;
    const JUMP_FORCE = -13;

    const GROUND_HEIGHT = 100;
    const CHAR_WIDTH = 100;
    const CHAR_HEIGHT = 180;

    const BASE_Y = 600;       
    const AMPLITUDE = 120;     

    const getActivePlatforms = () => {
        const width = window.innerWidth;
        const startLeft = -100;
        const endLeft = width - 30 - 544;

        return [
            { left: startLeft, right: startLeft + 520, top: 240 },                      
            { left: width * 0.28, right: (width * 0.28) + 285, top: platY.p1 }, // Fixed responsive sizing bounds             
            { left: width * 0.48, right: (width * 0.48) + 285, top: platY.p2 },             
            { left: width * 0.68, right: (width * 0.68) + 285, top: platY.p3 },             
            { left: endLeft, right: endLeft + 544, top: 520 }                           
        ];
    };

    const getTerminalBox = () => {
        const tWidth = 226;  
        const tHeight = 238; 
        return {
            left: window.innerWidth - 10 - tWidth, 
            top: 300,
            width: tWidth,
            height: tHeight
        };
    };

    const handleRetry = () => {
        velocity.current = { x: 0, y: 0 }; 
        keysPressed.current = {};          
        setPos({ x: START_X, y: START_Y });
        setIsDead(false);
        setIsWon(false);
        setIsPaused(false);
        setIsNearTerminal(false);
    };

    const handleTerminalClick = () => {
        if (isNearTerminal && !isPaused) {
            setIsWon(true);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => { keysPressed.current[e.key.toLowerCase()] = true; };
        const handleKeyUp = (e) => { keysPressed.current[e.key.toLowerCase()] = false; };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        let animationFrameId;

        const updateGame = () => {
            if (!isDead && !isWon && !isPaused) {
                gameTime.current += 0.02;
                setPlatY({
                    p1: BASE_Y + Math.sin(gameTime.current * 1.2) * AMPLITUDE,
                    p2: BASE_Y + Math.sin(gameTime.current * 0.8 + 2) * AMPLITUDE, 
                    p3: BASE_Y + Math.sin(gameTime.current * 1.5 + 4) * AMPLITUDE  
                });
            }

            setPos((prevPos) => {
                if (isDead || isWon || isPaused) return prevPos;

                let nextX = prevPos.x;
                let nextY = prevPos.y;

                if (keysPressed.current['a']) velocity.current.x = -MOVE_SPEED;
                else if (keysPressed.current['d']) velocity.current.x = MOVE_SPEED;
                else velocity.current.x = 0;

                velocity.current.y += GRAVITY;
                nextX += velocity.current.x;
                nextY += velocity.current.y;

                const charCenterX = nextX + (CHAR_WIDTH / 2);
                const tBox = getTerminalBox();
                const tCenterX = tBox.left + (tBox.width / 2);
                
                const nearX = Math.abs(charCenterX - tCenterX) < 140;
                const nearY = (nextY + CHAR_HEIGHT > tBox.top) && (nextY < tBox.top + tBox.height);

                setIsNearTerminal(nearX && nearY);

                let isGrounded = false;
                const groundTop = window.innerHeight - GROUND_HEIGHT;
                const currentPlatforms = getActivePlatforms();

                currentPlatforms.forEach(plat => {
                    const withinPlatformX = (nextX + CHAR_WIDTH > plat.left) && (nextX < plat.right);
                    const landingOnPlatform = (prevPos.y + CHAR_HEIGHT <= plat.top + 10) && (nextY + CHAR_HEIGHT >= plat.top);

                    if (withinPlatformX && landingOnPlatform && velocity.current.y >= 0) {
                        nextY = plat.top - CHAR_HEIGHT;
                        velocity.current.y = 0;
                        isGrounded = true;
                    }
                });

                if (nextY + CHAR_HEIGHT >= groundTop) {
                    setIsDead(true);
                }

                if (isGrounded && (keysPressed.current['w'] || keysPressed.current[' '])) {
                    velocity.current.y = JUMP_FORCE;
                }

                if (nextX < 0) nextX = 0;
                if (nextX > window.innerWidth - CHAR_WIDTH) nextX = window.innerWidth - CHAR_WIDTH;

                return { x: nextX, y: nextY };
            });

            animationFrameId = requestAnimationFrame(updateGame);
        };

        animationFrameId = requestAnimationFrame(updateGame);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            cancelAnimationFrame(animationFrameId);
        };
    }, [isDead, isWon, isPaused, platY]);

    return ( 
        <main className='Level3Main'>
            <div className='Character' style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
                <img className='CharacterImg' src={Character} alt="Character" />
            </div>

            <div className='StartPlatform'>
                <img src={StartPlat} alt="Start Platform" />
            </div>

            <div className='MovingPlatform1' style={{ top: `${platY.p1}px` }}>
                <img src={MovingPlat} alt="Moving Platform 1" />
            </div>

            <div className='MovingPlatform2' style={{ top: `${platY.p2}px` }}>
                <img src={MovingPlat} alt="Moving Platform 2" />
            </div>

            <div className='MovingPlatform3' style={{ top: `${platY.p3}px` }}>
                <img src={MovingPlat} alt="Moving Platform 3" />
            </div>

            <div className='EndPlatform'>
                <img src={EndPlat} alt="End Platform" />
            </div>
           
            <Terminal 
                TerminalImg={Control} 
                TerminalStyle={`ControlTerminal ${isNearTerminal ? 'Interactable' : ''}`}
                onClick={handleTerminalClick}
            />
            
            <div className='Ground'></div>

            {isDead && (
                <div className="LoseScreenContainer">
                    <LoseScreen RetryClick={handleRetry} />
                </div>
            )}
            
            {isWon && (
                <div className="WinScreenWrapper"> 
                    <WinScreen RetryClick={handleRetry} LevelPath="/ship" />
                </div>
            )}

            {/* Render Pause trigger interface toggles */}
            <Btn BtnIcon={Pause} BtnStyle="PauseBtn" onClick={() => setIsPaused(true)}/>
            {isPaused && <PauseMenu ResumeClick={() => setIsPaused(false)}/>}
        </main>
     );
}
 
export default Level3;